import { Component, effect, inject, input, ChangeDetectionStrategy } from '@angular/core';
import { BatchSignal, TreeSignal } from '@xanadu/signal';
import { Register } from '@xanadu/core';
import { BatchContent, BatchInfo, BatchResult, Folder, Genus, Identity, NodeModel } from '@xanadu/model';
import { ContentService } from '@xanadu/service';
import { firstValueFrom } from 'rxjs';
import { AuthSignal, TabSignal } from '@xanadu/signal';

@Component({
    selector: 'app-batch-tree',
    templateUrl: './batch-tree.component.html',
    styleUrls: ['./batch-tree.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
@Register('batch-tree')
export class BatchTreeComponent {
    private readonly treeSignal = inject(TreeSignal);
    private readonly batchSignal = inject(BatchSignal);
    private readonly tabSignal = inject(TabSignal);
    private readonly authSignal = inject(AuthSignal);
    private readonly contentService = inject(ContentService);

    model = input<any>();

    identity?: Identity;
    map: Map<any, NodeModel> = new Map<any, NodeModel>();
    genusModel?: NodeModel;
    genus?: Genus;

    rootModel?: NodeModel;

    constructor(
    ) {
        effect(() => {
            const root = this.treeSignal.contentRoot();

            if (root) {
                this.rootModel = root;
            }
        });

        effect(() => {
            this.identity = this.authSignal.identity();
        });

        effect(() => {
            const model = this.batchSignal.on();

            if (model?.info.genus?.prototype) {
                if (!(model.node instanceof Folder)) {
                    this.genusModel = model;
                }

                if (this.map.size <= 0) {
                    this.genus = model.info.genus;
                    this.batchSignal.prototype.set(model.info.genus?.prototypeId);
                }
                this.map.set(model.node._id, model);
                if (model.batch) {
                    model.batch.disabled = false;
                }

                this.notify(model);
            }
        });

        effect(() => {
            const model = this.batchSignal.off();

            if (model?.info.genus?.prototype) {
                if (this.genusModel === model) {
                    this.genusModel = undefined;
                }

                this.map.delete(model.node._id);

                if (this.map.size <= 0) {
                    this.genus = undefined;
                    this.batchSignal.clear();
                } else {
                    this.notify(model);
                }
            }
        });

        effect(() => {
            const content = this.batchSignal.save();

            if (content) {
                const ids: string[] = [];
                this.map.forEach((_, key) => ids.push(key));
                if (ids?.length) {
                    void firstValueFrom(this.contentService.batch(new BatchInfo(content, ids)))
                        .then(contents => {
                            const result = new BatchResult(this.genus?._id, contents);
                            this.map.forEach((_, key) => this.tabSignal.reset.content.set(key));
                            this.batchSignal.done.set(result);
                        });
                }
            }
        });
    }

    private notify(model: NodeModel): void {
        if (model.info.genus) {
            const folders = this.genusModel ? undefined : this.getFolders();
            this.batchSignal.content.set(new BatchContent(model.info.genus, model.site, folders));
        }
    }

    private getFolders(): Folder[] {
        let result: Folder[] = [];
        for (let model of this.map.values()) {
            if (model.node instanceof Folder) {
                result.push(model.node);
            }
        }
        return result;
    }
}
